import React, { useEffect, useState } from "react";
import { ErrorSection, Props, errTypeAndMsg } from "./HomeDashboard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useGetPermissions } from "helpers/useGetPermissions";
import { permissionsRequested } from "components/Category/Category";
import { endpoints } from "config";
import api from "api";
import { ApiResponse } from "@avamae/formbuilder/dist/FormBuilder";
import { SimpleProjectCard } from "./components/SimpleProjectCard";
import { Link } from "react-router-dom";
import { Spinner } from "components/Spinner/Spinner";
import { SwiperButtons } from "./SwiperButtons";
import { useWindowSize } from "helpers/useWindowSize";

export type LastViewedProjectsProps = { access?: boolean };

export type Data = {
  programmes__Id: number | null;
  programmes__Name: string | null;
  sites__Name: string | null;
  projectStatusTypes__Status: string | null;
  projects__CompletedGateways: number | null;
  projects__TotalGateways: number | null;
  projects__Id: number | null;
  sites__Id: number | null;
  sites__ClientId: string | null;
};

export type ViewedProjectData = {
  programmes__Id: number | null;
  programmes__Name: string | null;
  sites__Name: string | null;
  projectStatusTypes__Status: string | null;
  projects__CompletedGateways: number | null;
  projects__TotalGateways: number | null;
  projects__Id: number | null;
  sites__Id: number | null;
  sites__ClientId: string | null;
};

const translateError = (errorCode: string) => {
  if (errTypeAndMsg[errorCode as keyof typeof errTypeAndMsg])
    return errTypeAndMsg[errorCode as keyof typeof errTypeAndMsg];
  switch (errorCode) {
    case "No_Access_Permission":
      return errTypeAndMsg.noAccessPermission;
    case "Generic_Error":
      return errTypeAndMsg.generic;
    default:
      return errTypeAndMsg.generic;
  }
};

const NUMBER_OF_CARDS_1600 = 5;
const NUMBER_OF_CARDS_1200 = 4;
const NUMBER_OF_CARDS_768 = 3;
const NUMBER_OF_CARDS_568 = 2;
const NUMBER_OF_CARDS_480 = 1;

const LastViewedProjectsCarousel = (props: LastViewedProjectsProps & Props) => {
  const [loading, setLoading] = useState(true);
  const [viewedProjects, setViewedProjects] = useState<ViewedProjectData[]>([]);
  const [error, setError] = useState<any>(null);
  const [disableScrollLeft, setDisableScrollLeft] = useState<boolean>(false);
  const [disableScrollRight, setDisableScrollRight] = useState<boolean>(false);

  const [placeholder, setPlaceholder] = useState<number | null>(null);
  const [slidesPerView, setSlidesPerView] = useState<any>(null);

  const [width, height] = useWindowSize(); // Listen for window resizing

  const [numberOfCards, setNumberOfCards] = useState<number>(6);

  useEffect(() => {
    if (width >= 1600) {
      setNumberOfCards(NUMBER_OF_CARDS_1600);
    } else if (width < 1600 && width >= 1200) {
      setNumberOfCards(NUMBER_OF_CARDS_1200);
    } else if (width < 1200 && width >= 768) {
      setNumberOfCards(NUMBER_OF_CARDS_768);
    } else if (width < 768 && width >= 568) {
      setNumberOfCards(NUMBER_OF_CARDS_568);
    } else if (width < 568) {
      setNumberOfCards(NUMBER_OF_CARDS_480);
    }
  }, [width]);

  useEffect(() => {
    const viewedProjectsLength = viewedProjects ? viewedProjects.length : 0;

    // if (placeholder === null) {
    if (viewedProjectsLength < numberOfCards) {
      const tempPlaceholder = numberOfCards - viewedProjectsLength;

      setPlaceholder(tempPlaceholder);
    } else {
      setPlaceholder(0);
    }
    // }
  }, [viewedProjects, numberOfCards]);

  useEffect(() => {
    if (!props.access) return;
    api
      .get<ApiResponse<Data[]>>(endpoints.home.lastViewedProjects)
      .then(res => {
        const tempViewedProjects: any = res?.data?.details?.listData?.map(
          (d: Data) => ({
            programmes__Id: d.programmes__Id,
            programmes__Name: d.programmes__Name,
            sites__Name: d.sites__Name,
            projectStatusTypes__Status: d.projectStatusTypes__Status,
            projects__CompletedGateways: d.projects__CompletedGateways,
            projects__TotalGateways: d.projects__TotalGateways,
            projects__Id: d.projects__Id,
            sites__Id: d.sites__Id,
            sites__ClientId: d.sites__ClientId,
          })
        );

        setViewedProjects(tempViewedProjects);
        setLoading(false);
      })
      .catch(err => {
        if (
          err.response &&
          err.response.data?.errors &&
          Array.isArray(err.response.data?.errors) &&
          err.response.data?.errors?.length > 0
        )
          setError(
            err.response.data?.errors[0]?.messageCode ?? "Generic_Error"
          );
        else setError("Generic_Error");
      });
  }, []);

  const permissions = useGetPermissions(
    permissionsRequested,
    "/projectmanagement"
  ); //['view'], '/projecst'

  const hasPermission = permissions.get("overview");

  const breakPoints = {
    1: {
      slidesPerView: NUMBER_OF_CARDS_480,
    },
    320: {
      slidesPerView: NUMBER_OF_CARDS_480,
    },
    480: {
      slidesPerView: NUMBER_OF_CARDS_480,
    },
    568: {
      slidesPerView: NUMBER_OF_CARDS_568,
    },
    768: {
      slidesPerView: NUMBER_OF_CARDS_768,
    },
    1200: {
      slidesPerView: NUMBER_OF_CARDS_1200,
    },
    1600: {
      slidesPerView: NUMBER_OF_CARDS_1600,
    },
  };

  const handlePlaceholder = (slidesPerView: any, noOfSlides: any) => {
    const placeholdersNeeded = slidesPerView - noOfSlides;
    if (placeholdersNeeded > 0) {
      setPlaceholder(placeholdersNeeded);
    } else {
      setPlaceholder(0);
    }
  };

  return (
    <div className="HomeSection ProjectsSection">
      <div className="SectionHeader">
        <h4 style={{ marginBottom: "0" }}>LAST VIEWED PROJECTS</h4>

        <Link to="/projectmanagement">view all projects</Link>
      </div>
      <div
        className={`ProjectCardsWrapper ${loading ? "HomeSectionLoading" : ""}`}
      >
        {error || !props.access ? (
          <ErrorSection style={{ minHeight: "140px", height: "170px" }}>
            {translateError(!props.access ? "No_Access_Permission" : error)}
          </ErrorSection>
        ) : loading ? (
          <Spinner positionAbsolute />
        ) : (
          <>
            <Swiper
              spaceBetween={10}
              // slidesPerView={numberOfCards}
              updateOnWindowResize
              navigation={{
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
                disabledClass: "swiper-button-disabled",
              }}
              observer={true} // updates swiper each time the style or children are changed
              observeParents={true} // watches for mutations of swiper parent elements
              observeSlideChildren={true} // watches for mutations of swiper children elements
              modules={[Navigation, Pagination]}
              breakpoints={breakPoints}
              onReachBeginning={() => {
                setDisableScrollLeft(true);
              }}
              onReachEnd={() => {
                setDisableScrollRight(true);
              }}
            >
              {viewedProjects &&
                viewedProjects.map((item, idx) => {
                  return (
                    <SwiperSlide key={`swiper-slide-${idx}`}>
                      <SimpleProjectCard
                        {...viewedProjects[idx]}
                        permission={hasPermission}
                        key={idx + "-" + viewedProjects[idx]?.projects__Id ?? 0}
                      />
                    </SwiperSlide>
                  );
                })}

              {placeholder &&
                [...Array(placeholder)].map((item, idx) => {
                  return (
                    <SwiperSlide key={`swiper-slide-placeholder-${idx}`}>
                      <div
                        className="PlaceholderSlide"
                        key={`swiper-slide-placeholder-slide-${idx}`}
                      ></div>
                    </SwiperSlide>
                  );
                })}

              <SwiperButtons
                disableScrollLeft={disableScrollLeft}
                setDisableScrollLeft={setDisableScrollLeft}
                disableScrollRight={disableScrollRight}
                setDisableScrollRight={setDisableScrollRight}
              />
            </Swiper>
          </>
        )}
      </div>
    </div>
  );
};

export default LastViewedProjectsCarousel;
